import { z } from 'zod';
import { platformJsonValueSchema } from '@crest/platform-contracts/settings';
import { platformImportResolutionProofSchema } from '@crest/platform-contracts/import-resolution';
import { projectsImportResolutionSchema } from '@crest/module-projects/contracts/import-resolution';
export const communicationsLegacyMessageImportCapability = 'communications.legacy-message-import.v1';
export const communicationsLegacyMessageImportMappingVersion = 'communications-legacy-message-normalized-v1';
/** Historical rehearsal baseline. Restricted runs bind their current bounded counts and source hash. */
export const communicationsLegacyMessageWaterProCounts = {
    email_threads: 37,
    email_messages: 48,
    sms_messages: 4,
};
export const communicationsLegacyMessageWaterProTotal = 89;
const uuid = z.string().uuid();
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const sourceId = z.string().trim().min(1).max(500);
const sourceTimestamp = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z$/);
const tenantKey = z.string().trim().min(1).max(240);
const boundedSourceCount = z.number().int().nonnegative().safe().max(100_000);
export const communicationsLegacyMessageSourceCountsSchema = z.strictObject({
    email_threads: boundedSourceCount.positive(),
    email_messages: boundedSourceCount,
    sms_messages: boundedSourceCount,
}).superRefine((value, context) => {
    if (value.email_threads + value.email_messages + value.sms_messages > 100_000) {
        context.addIssue({ code: 'custom', message: 'Communications source counts exceed the bounded import size.' });
    }
});
export const communicationsLegacyEmailThreadSchema = z.strictObject({
    id: uuid, project_id: uuid.nullable(), contact_id: uuid.nullable(), subject: z.string().nullable(),
    participants: z.array(z.string()).nullable(), last_message_at: sourceTimestamp.nullable(),
    message_count: z.number().int().nonnegative().nullable(), created_at: sourceTimestamp,
    email_account_id: uuid.nullable(), is_read: z.boolean().nullable(), is_starred: z.boolean().nullable(), tenant_id: uuid,
});
export const communicationsLegacyEmailMessageSchema = z.strictObject({
    id: uuid, thread_id: uuid, message_uid: z.string().min(1), from_address: z.string().min(1),
    to_addresses: z.array(z.string()).nullable(), cc_addresses: z.array(z.string()).nullable(),
    subject: z.string().nullable(), body_text: z.string().nullable(), body_html: z.string().nullable(),
    sent_at: sourceTimestamp.nullable(), direction: z.enum(['inbound', 'outbound']).nullable(),
    has_attachments: z.boolean().nullable(), created_at: sourceTimestamp, email_account_id: uuid.nullable(),
    in_reply_to: z.string().nullable(), references_header: z.array(z.string()).nullable(),
    raw_headers: platformJsonValueSchema.nullable(), tenant_id: uuid,
});
export const communicationsLegacySmsMessageSchema = z.strictObject({
    id: uuid, project_id: uuid.nullable(), contact_id: uuid.nullable(), phone_number: z.string().min(1),
    direction: z.enum(['inbound', 'outbound']), body: z.string().min(1),
    status: z.enum(['sent', 'delivered', 'failed', 'received']).nullable(), twilio_sid: z.string().nullable(),
    sent_by: uuid.nullable(), created_at: sourceTimestamp, error_code: z.string().nullable(),
    error_message: z.string().nullable(), delivered_at: sourceTimestamp.nullable(), updated_at: sourceTimestamp,
    tenant_id: uuid, metadata: platformJsonValueSchema,
});
const emailThreadRow = z.strictObject({ sequence: z.number().int().positive(), sourceTable: z.literal('email_threads'), sourceId,
    sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, payload: communicationsLegacyEmailThreadSchema });
const emailMessageRow = z.strictObject({ sequence: z.number().int().positive(), sourceTable: z.literal('email_messages'), sourceId,
    sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, payload: communicationsLegacyEmailMessageSchema });
const smsMessageRow = z.strictObject({ sequence: z.number().int().positive(), sourceTable: z.literal('sms_messages'), sourceId,
    sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, payload: communicationsLegacySmsMessageSchema });
export const communicationsLegacyMessageImportRowSchema = z.discriminatedUnion('sourceTable', [emailThreadRow, emailMessageRow, smsMessageRow])
    .superRefine((value, context) => {
    if (value.sourceId !== value.payload.id)
        context.addIssue({ code: 'custom', path: ['sourceId'], message: 'Source ID must equal payload ID.' });
});
export const communicationsLegacyMessageMembershipSourceMappingSchema = z.strictObject({
    sourceTenantExternalKey: tenantKey, sourceUserId: uuid, membershipSourceExternalKey: sourceId,
});
export const communicationsLegacyMessageMembershipResolutionSchema = z.strictObject({
    sourceUserId: uuid, membershipSourceExternalKey: sourceId, sourceTenantExternalKey: tenantKey,
    proof: platformImportResolutionProofSchema, verified: z.literal(true),
}).superRefine((value, context) => {
    if (value.proof.entityType !== 'membership' || value.proof.sourceId !== value.membershipSourceExternalKey
        || value.proof.sourceTenantExternalKey !== value.sourceTenantExternalKey) {
        context.addIssue({ code: 'custom', path: ['proof'], message: 'Membership proof must bind the mapped source membership.' });
    }
});
export const communicationsLegacyMessageContactResolutionSchema = z.strictObject({
    sourceContactId: uuid, sourceTenantExternalKey: tenantKey, verified: z.literal(true),
    proof: z.strictObject({
        capability: z.literal('relationships.import-resolution.v1'), entityType: z.literal('contact'),
        sourceId: uuid, targetId: uuid, sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp,
        runId: uuid, targetSha256: sha256,
    }),
}).superRefine((value, context) => {
    if (value.sourceContactId !== value.proof.sourceId)
        context.addIssue({ code: 'custom', path: ['proof'], message: 'Contact proof must bind source contact.' });
});
export const communicationsLegacyMessageProjectResolutionSchema = z.strictObject({
    sourceProjectId: uuid, sourceTenantExternalKey: tenantKey, verified: z.literal(true),
    proof: projectsImportResolutionSchema,
}).superRefine((value, context) => {
    if (value.sourceProjectId !== value.proof.sourceId || value.proof.entityType !== 'project') {
        context.addIssue({ code: 'custom', path: ['proof'], message: 'Project proof must bind source project.' });
    }
});
export const communicationsLegacyMessageImportSnapshotSchema = z.strictObject({
    contractVersion: z.literal(communicationsLegacyMessageImportCapability),
    mappingVersion: z.literal(communicationsLegacyMessageImportMappingVersion),
    sourceSystem: z.literal('crest-crm'), sourceTenantId: uuid, tenantExternalKey: z.literal('water-pro'),
    targetTenantId: uuid, sourceEpoch: z.string().trim().min(1).max(240), capturedAt: z.string().datetime({ offset: true }),
    sourceSha256: sha256,
    sourceCounts: communicationsLegacyMessageSourceCountsSchema,
    membershipSourceMappings: z.array(communicationsLegacyMessageMembershipSourceMappingSchema).max(10_000),
    rows: z.array(z.unknown()).min(1).max(100_000),
}).superRefine((value, context) => {
    const expectedRows = value.sourceCounts.email_threads + value.sourceCounts.email_messages + value.sourceCounts.sms_messages;
    if (value.rows.length !== expectedRows) {
        context.addIssue({ code: 'custom', path: ['rows'], message: 'Source rows must equal the explicitly bound table counts.' });
    }
});
const targetThread = z.strictObject({
    id: uuid, channel: z.enum(['email', 'sms']), contactId: uuid, projectId: uuid.nullable(), subject: z.string().max(240).nullable(),
    createdByMembershipId: uuid, updatedByMembershipId: uuid, createdAt: sourceTimestamp, updatedAt: sourceTimestamp,
});
const historicalEmailRecipients = z.strictObject({
    from_address: z.string(),
    to_addresses: z.array(z.string()).nullable(),
    cc_addresses: z.array(z.string()).nullable(),
});
const targetMessage = z.strictObject({
    id: uuid, threadId: uuid, direction: z.enum(['inbound', 'outbound']), state: z.enum(['sent', 'delivered', 'failed']),
    body: z.string().trim().min(1).max(10_000), destination: z.string().max(320).nullable(),
    sentAt: sourceTimestamp.nullable(), deliveredAt: sourceTimestamp.nullable(),
    createdByMembershipId: uuid, createdAt: sourceTimestamp, updatedAt: sourceTimestamp,
    sourceReference: z.strictObject({ capability: z.literal(communicationsLegacyMessageImportCapability),
        resourceType: z.literal('communications.legacy-message'), resourceId: uuid, purpose: z.literal('historical-import'),
        legacyRecipients: historicalEmailRecipients.optional() }),
});
export const communicationsLegacyMessageImportActionSchema = z.strictObject({
    sequence: z.number().int().positive(), sourceTable: z.enum(['email_threads', 'email_messages', 'sms_messages']),
    sourceId, sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, targetTenantId: uuid,
    targetKind: z.enum(['thread', 'message']), targetId: uuid, identityStrategy: z.enum(['preserved-uuid', 'deterministic-v5']),
    thread: targetThread.nullable(), message: targetMessage.nullable(), sourceEvidence: platformJsonValueSchema,
    contactResolution: communicationsLegacyMessageContactResolutionSchema.nullable(),
    projectResolution: communicationsLegacyMessageProjectResolutionSchema.nullable(),
    actorResolution: communicationsLegacyMessageMembershipResolutionSchema.nullable(),
}).superRefine((value, context) => {
    if ((value.targetKind === 'thread') !== (value.thread !== null && value.message === null)) {
        context.addIssue({ code: 'custom', path: ['targetKind'], message: 'Thread actions carry exactly one thread.' });
    }
    if (value.targetKind === 'message' && value.message === null)
        context.addIssue({ code: 'custom', path: ['message'], message: 'Message action requires a message.' });
});
export const communicationsLegacyMessageImportRejectSchema = z.strictObject({
    sequence: z.number().int().positive(), sourceTable: z.enum(['email_threads', 'email_messages', 'sms_messages']), sourceId,
    sourceSha256: sha256, reasonCode: z.enum([
        'schema-invalid', 'source-tenant-mismatch', 'duplicate-source-identity', 'source-after-capture',
        'unresolved-contact', 'unresolved-project', 'unresolved-actor', 'unresolved-thread', 'thread-key-conflict', 'thread-key-alias',
        'unsupported-html', 'unsupported-attachment', 'unsupported-recipient-shape', 'unsupported-body',
        'unsupported-direction', 'unsupported-delivery-state', 'unsupported-value',
    ]), fieldPaths: z.array(z.string().trim().min(1).max(240)).min(1).max(30),
});
export const communicationsLegacyMessageThreadAliasSchema = z.strictObject({
    sequence: z.number().int().positive(), sourceTable: z.literal('email_threads'), sourceId: uuid,
    sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, targetTenantId: uuid,
    canonicalSourceThreadId: uuid, canonicalTargetThreadId: uuid,
    sourceEvidence: communicationsLegacyEmailThreadSchema,
    contactResolution: communicationsLegacyMessageContactResolutionSchema,
    projectResolution: communicationsLegacyMessageProjectResolutionSchema.nullable(),
}).superRefine((value, context) => {
    if (value.sourceId !== value.sourceEvidence.id || value.sourceId === value.canonicalSourceThreadId
        || value.canonicalTargetThreadId !== value.canonicalSourceThreadId) {
        context.addIssue({ code: 'custom', path: ['canonicalTargetThreadId'], message: 'Thread alias must bind a distinct source thread to the preserved canonical thread.' });
    }
});
export const communicationsLegacyMessageImportPlanSchema = z.strictObject({
    contractVersion: z.literal(communicationsLegacyMessageImportCapability), mappingVersion: z.literal(communicationsLegacyMessageImportMappingVersion),
    sourceSystem: z.literal('crest-crm'), sourceTenantId: uuid, tenantExternalKey: z.literal('water-pro'), targetTenantId: uuid,
    sourceEpoch: z.string().trim().min(1).max(240), capturedAt: z.string().datetime({ offset: true }), sourceSha256: sha256,
    planSha256: sha256, sourceCounts: communicationsLegacyMessageSourceCountsSchema,
    actions: z.array(communicationsLegacyMessageImportActionSchema), rejects: z.array(communicationsLegacyMessageImportRejectSchema),
    threadAliases: z.array(communicationsLegacyMessageThreadAliasSchema).default([]),
    counts: z.strictObject({ sourceRows: z.number().int().positive().safe().max(100_000), plannedRows: z.number().int().nonnegative(), rejectedRows: z.number().int().nonnegative() }),
}).superRefine((value, context) => {
    const expectedRows = value.sourceCounts.email_threads + value.sourceCounts.email_messages + value.sourceCounts.sms_messages;
    if (expectedRows !== value.counts.sourceRows || value.actions.length + value.rejects.length !== value.counts.sourceRows || value.actions.length !== value.counts.plannedRows
        || value.rejects.length !== value.counts.rejectedRows)
        context.addIssue({ code: 'custom', path: ['counts'], message: 'Every source row must be planned or quarantined.' });
    const aliasRejects = new Set(value.rejects.filter((reject) => reject.reasonCode === 'thread-key-alias')
        .map((reject) => `${reject.sequence}\0${reject.sourceId}\0${reject.sourceSha256}`));
    const aliases = new Set(value.threadAliases.map((alias) => `${alias.sequence}\0${alias.sourceId}\0${alias.sourceSha256}`));
    if (aliasRejects.size !== aliases.size || [...aliasRejects].some((key) => !aliases.has(key))) {
        context.addIssue({ code: 'custom', path: ['threadAliases'], message: 'Every canonical thread alias must retain one matching quarantine disposition.' });
    }
    const canonicalThreads = new Set(value.actions.filter((action) => action.targetKind === 'thread')
        .map((action) => `${action.sourceId}\0${action.targetId}`));
    if (value.threadAliases.some((alias) => !canonicalThreads.has(`${alias.canonicalSourceThreadId}\0${alias.canonicalTargetThreadId}`))) {
        context.addIssue({ code: 'custom', path: ['threadAliases'], message: 'Thread aliases require a canonical thread action in the same plan.' });
    }
});
export const communicationsLegacyMessageImportReceiptSchema = z.strictObject({
    sequence: z.number().int().positive(), sourceTable: z.enum(['email_threads', 'email_messages', 'sms_messages']),
    sourceId, sourceSha256: sha256, sourceUpdatedAt: sourceTimestamp, targetKind: z.enum(['thread', 'message']), targetId: uuid,
    outcome: z.enum(['applied', 'skipped']), targetSha256: sha256, targetCanonical: platformJsonValueSchema,
    previousIdentity: platformJsonValueSchema.nullable(), rollbackToken: platformJsonValueSchema,
});
export const communicationsLegacyMessageImportRollbackReceiptSchema = z.strictObject({
    sequence: z.number().int().positive(), outcome: z.enum(['applied', 'already-rolled-back', 'rejected-conflict', 'rejected-state']),
    detailCode: z.string().trim().min(3).max(120), targetSha256: sha256.nullable(),
});
//# sourceMappingURL=communications-legacy-message-import.contract.js.map